import { Currency, currencySymbol, Money } from "@hubrise/react-components"
import * as React from "react"

import useStatefulField from "shared/hooks/useStatefulField"

import { BaseCell, BaseCellProps } from "../shared/BaseCell"
import { BaseInput, BaseInputPrefix, BaseInputContent } from "../shared/Styles.BaseInput"

export interface MoneyCellProps extends BaseCellProps {
  currency: Currency
  initialValue: Money | null
  placeholder?: Money
  isRequired: boolean
  onChange: (value: Money | null) => void
}

const stringToMoney = (value: string, currency: Currency): Money | null =>
  value.length > 0 ? new Money(Number(value) * 100, currency) : null

const MoneyCell = ({
  currency,
  initialValue,
  placeholder,
  isRequired,
  onChange,
  ...BaseCellProps
}: MoneyCellProps): JSX.Element => {
  const { value, handleChange, handleBlur } = useStatefulField({
    initialValue: initialValue?.toStringNoCurrency() ?? "",
    onChange: (value) => onChange(stringToMoney(value, currency)),
  })

  const [hasFocus, setHasFocus] = React.useState(false)
  const $inputRef = React.useRef<HTMLInputElement>(null)

  const handleChangeForMoney = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value
    if (INPUT_REGEXP.test(value) && (value.length > 0 || !isRequired)) {
      handleChange(value)
    }
  }

  const handleFocusForMoney = (): void => {
    if ($inputRef?.current) $inputRef.current.select()
    setHasFocus(true)
  }

  const handleBlurForMoney = (): void => {
    handleChange(stringToMoney(value, currency)?.toStringNoCurrency() ?? "")
    setHasFocus(false)
    handleBlur()
  }

  // Since the input value is automatically selected on focus, we need to prevent dragging to avoid accidental loss of
  // the selection.
  const preventDrag = (event: React.DragEvent<HTMLInputElement>): void => {
    event.preventDefault()
  }

  return (
    <BaseCell {...BaseCellProps}>
      <BaseInputContent hasFocus={hasFocus}>
        {(hasFocus || placeholder !== undefined || value !== "") && (
          <BaseInputPrefix>{currencySymbol(currency)}</BaseInputPrefix>
        )}

        <BaseInput
          ref={$inputRef}
          value={value}
          placeholder={placeholder?.toStringNoCurrency()}
          onChange={handleChangeForMoney}
          onFocus={handleFocusForMoney}
          onBlur={handleBlurForMoney}
          onDragStart={preventDrag}
        />
      </BaseInputContent>
    </BaseCell>
  )
}

const INPUT_REGEXP = /^\d{0,8}\.?\d{0,2}$/

export default MoneyCell
